import { ILogger } from '@vp/pdp-ubik-logging';

import { ScenePurposes } from '../../../constants/DesignPurposes';
import { ICombinationMedia, ICurrentDocument } from '../../../types/interfaces';
import { buildInstructionsUri } from '../libs/instructionsUrlBuilder';
import { checkWillDocumentOpen } from '../libs/isResourceAvailable';

import { Combination } from './Combination';
import { Image } from './Image';
import { Slide } from './Slide';
import { SlideArray } from './SlideArray';
export class CombinationMedia {
  private constructor(
    private _combination: Combination,
    private _slides: SlideArray
  ) {}

  get thumbnails(): string[] {
    return this._slides.thumbnails;
  }

  hasCompatibleCombination(combination: Combination): boolean {
    return this._combination.isCompatible(combination);
  }

  get combination(): Combination {
    return this._combination;
  }

  get medias(): SlideArray {
    return this._slides;
  }

  get containsVideos(): boolean {
    return this._slides.containsVideos;
  }

  copy(): CombinationMedia {
    return new CombinationMedia(this._combination.copy(), this._slides);
  }

  static from(combinationMedia: ICombinationMedia): CombinationMedia {
    return new CombinationMedia(
      new Combination(combinationMedia.combination),
      SlideArray.from(combinationMedia.medias)
    );
  }

  static fromClasses(combination: Combination, slides: SlideArray): CombinationMedia {
    return new CombinationMedia(combination, slides);
  }

  public async createDocumentSlide(
    imagesTenant: string,
    locale: string,
    productKey: string,
    productVersion: number,
    currentDocument: ICurrentDocument,
    logger: ILogger
  ): Promise<boolean> {
    const slide = await this.createSlide(
      imagesTenant,
      locale,
      productKey,
      productVersion,
      currentDocument,
      logger
    );

    if (!slide || !slide.media) {
      return false;
    }

    this._slides.addFirst(slide);

    return true;
  }

  private async createSlide(
    imagesTenant: string,
    locale: string,
    productKey: string,
    productVersion: number,
    currentDocument: ICurrentDocument,
    logger: ILogger
  ): Promise<Slide | null> {
    const documentUri = this._combination.getDocumentUrlAdaptedToOptions(
      locale,
      productKey,
      productVersion,
      currentDocument.documentUri
    );

    const willDocumentOpen = await checkWillDocumentOpen(documentUri);
    if (!willDocumentOpen) {
      logger.error('Document not available', {
        documentUri,
        productKey,
        productVersion,
      });
      return null;
    }

    const instructionsUri = buildInstructionsUri(documentUri);

    const image = Image.fromDocument(
      imagesTenant,
      locale,
      productKey,
      productVersion,
      currentDocument.altText,
      this._combination.optionSelections,
      instructionsUri,
      ScenePurposes.StudioReview
    );

    if (!image) return null;

    return new Slide(image);
  }
}
